import clsx from "clsx"
import { useState } from "react"
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid"
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
  Label,
} from "@headlessui/react"

const Combobox = ({
  label,
  placeholder,
  options,
  playerName,
  onChange,
  alignOptions = "bottom",
}: {
  label: string
  placeholder: string
  options: string[]
  playerName: string | undefined
  onChange: (targetName: string | undefined) => void
  alignOptions?: "top" | "bottom"
}) => {
  const [query, setQuery] = useState("")

  const filteredPlayers =
    query === ""
      ? options
      : options.filter((player) =>
          player.toLowerCase().includes(query.toLowerCase())
        )

  return (
    <HeadlessCombobox
      as="div"
      defaultValue={playerName}
      onChange={(player: string) => onChange(player ?? undefined)}
    >
      <Label className="sr-only">{label}</Label>
      <div className="relative">
        <ComboboxInput
          className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 pr-8 text-sm text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
          onChange={(event) => setQuery(event.target.value)}
          displayValue={(player: string) => player}
          placeholder={placeholder}
        />

        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </ComboboxButton>

        <ComboboxOptions
          className={clsx(
            "absolute z-10 max-h-40 w-full overflow-auto rounded-lg bg-gray-50 py-1.5 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm",
            {
              "mt-1": alignOptions === "bottom",
              "-bottom-full -mb-1 -translate-y-1/2": alignOptions === "top",
            }
          )}
        >
          {query.length > 0 && (
            <ComboboxOption
              value={query}
              className="data-focus:bg-chessdrop-600 relative cursor-default py-2 pr-9 pl-3 text-sm text-gray-900 select-none data-focus:text-white"
            >
              {({ selected }) => (
                <span
                  className={clsx(
                    "block truncate",
                    selected && "font-semibold"
                  )}
                >
                  Create &quot;{query}&quot;
                </span>
              )}
            </ComboboxOption>
          )}
          {filteredPlayers.map((player) => (
            <ComboboxOption
              key={player}
              value={player}
              className="data-focus:bg-chessdrop-600 relative cursor-default py-2 pr-9 pl-3 text-sm text-gray-900 select-none data-focus:text-white"
            >
              {({ focus, selected }) => (
                <>
                  <span
                    className={clsx(
                      "block truncate",
                      selected && "font-semibold"
                    )}
                  >
                    {player}
                  </span>

                  {selected && (
                    <span
                      className={clsx(
                        "absolute inset-y-0 right-0 flex items-center pr-4",
                        focus ? "text-white" : "text-chessdrop-600"
                      )}
                    >
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  )
}

export default Combobox
